import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
  Flex,
  FlexItem,
  Switch,
  ToolbarItem,
} from '@patternfly/react-core';
import { ExpandableRowContent, Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { useMollieTransactions } from '../../../hooks/useAdminReconciliation';
import type { MollieTransaction } from '../../../types/models/Reconciliation';
import { formatBareDateNumeric, formatInstantBrussels } from '../../../utils/dateFormat';
import { StatusMultiSelect } from '../Verda/VerdaGpuParts';
import {
  AMOUNT_CELL,
  CheckLabel,
  ChoiceFilter,
  CsvButton,
  EmptyListState,
  FetchedAt,
  FlagLabels,
  ListPagination,
  ListToolbar,
  LiveReadSpinner,
  Money,
  MonthPicker,
  NoMatchState,
  ReadErrorState,
  ReconciliationBanners,
  RefreshButton,
  SettlementRef,
  StaleDataAlert,
  distinctOptions,
  matchesQuery,
  useClientList,
} from './ReconciliationParts';
import { DetailList, attributionItems } from './AttributionDetails';
import { PAYMENT_METHOD_LABELS, PURPOSE_LABELS, TRANSACTION_TYPE_LABELS, labelOf } from './reconciliationLabels';
import { buildJournalCsv, reconciliationCsvFilename } from './reconciliationCsv';

/** Refunds and chargebacks link to the payout that DEDUCTED them. */
const DEDUCTED_TYPES = new Set(['refund', 'chargeback']);
/** The « Objet » filter value for rows that are not about a payment (payouts, compensations). */
const NO_PURPOSE = '__none__';
/** The « Versement » filter value for rows not paid out yet. */
const NO_SETTLEMENT = '__none__';

/** The broad search: client, invoice no., descriptions, Mollie ids, domains, payout reference. */
export const matchTransaction = (t: MollieTransaction, q: string) =>
  matchesQuery(q, [
    t.client_name,
    t.invoice_numero,
    t.payment_description,
    t.refund_description,
    t.chargeback_description,
    t.id,
    t.payment_id,
    t.refund_id,
    t.chargeback_id,
    t.settlement_id,
    t.settlement_reference,
    t.mollie_invoice_reference,
    t.domains,
  ]);

/** What the row is about, in one cell: invoice number, domain order, or the payment's description. */
function referenceText(t: MollieTransaction): string {
  if (t.invoice_numero) {
    return `Facture ${t.invoice_numero}`;
  }
  if (t.domain_order_id !== null) {
    return t.domains.length > 0
      ? `Commande #${t.domain_order_id} — ${t.domains.join(', ')}`
      : `Commande #${t.domain_order_id}`;
  }
  if (t.mollie_invoice_reference) {
    return `Facture Mollie ${t.mollie_invoice_reference}`;
  }
  return t.payment_description ?? t.refund_description ?? t.chargeback_description ?? '—';
}

const COLUMN_COUNT = 11;

interface MollieJournalTabProps {
  month: string;
  onMonthChange: (month: string) => void;
  isActive: boolean;
  /** « Signalées seulement » — controlled by the page so the overview can open the journal on it. */
  flaggedOnly: boolean;
  onFlaggedOnlyChange: (value: boolean) => void;
}

/**
 * Journal Mollie — `GET /admin/reconciliation/mollie/transactions`: every movement of the Mollie balance
 * booked in the month, attributed to its purpose / client / sales invoice, with its payout.
 */
const MollieJournalTab: React.FunctionComponent<MollieJournalTabProps> = ({
  month,
  onMonthChange,
  isActive,
  flaggedOnly,
  onFlaggedOnlyChange,
}) => {
  const { data, loading, error, fetchedAt, refetch } = useMollieTransactions(month, isActive);
  const rows = useMemo(() => data?.transactions ?? [], [data]);
  const [types, setTypes] = useState<string[]>([]);
  const [purposes, setPurposes] = useState<string[]>([]);
  const [settlement, setSettlement] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());

  const typeOptions = useMemo(
    () =>
      distinctOptions(
        // A selection kept from another month stays listed, so it can still be unticked.
        [...rows.map((r) => r.type), ...types],
        (v) => labelOf(TRANSACTION_TYPE_LABELS, v),
      ),
    [rows, types],
  );
  const purposeOptions = useMemo(() => {
    const options = distinctOptions([...rows.map((r) => r.purpose), ...purposes.filter((p) => p !== NO_PURPOSE)], (v) =>
      labelOf(PURPOSE_LABELS, v),
    );
    return rows.some((r) => r.purpose === null) || purposes.includes(NO_PURPOSE)
      ? [...options, { value: NO_PURPOSE, label: 'Sans objet' }]
      : options;
  }, [rows, purposes]);
  const settlementOptions = useMemo(() => {
    const options = distinctOptions(
      rows.map((r) => r.settlement_id),
      (id) => rows.find((r) => r.settlement_id === id)?.settlement_reference ?? id,
    );
    return rows.some((r) => r.settlement_id === null)
      ? [...options, { value: NO_SETTLEMENT, label: 'Pas encore versé' }]
      : options;
  }, [rows]);

  // A payout belongs to one month's journal: its filter does not carry over to another month.
  useEffect(() => {
    setSettlement(null);
  }, [month]);

  const predicate = useCallback(
    (t: MollieTransaction) =>
      (types.length === 0 || types.includes(t.type)) &&
      (purposes.length === 0 || purposes.includes(t.purpose ?? NO_PURPOSE)) &&
      (settlement === null || (t.settlement_id ?? NO_SETTLEMENT) === settlement) &&
      (!flaggedOnly || t.flags.length > 0),
    [types, purposes, settlement, flaggedOnly],
  );
  const filterKey =
    types.length + purposes.length > 0 || settlement !== null || flaggedOnly
      ? JSON.stringify([types, purposes, settlement, flaggedOnly])
      : '';
  const list = useClientList(rows, matchTransaction, predicate, filterKey);

  const clearFilters = () => {
    list.setSearchValue('');
    setTypes([]);
    setPurposes([]);
    setSettlement(null);
    onFlaggedOnlyChange(false);
  };

  const rowKey = (t: MollieTransaction, index: number) => t.id ?? `${t.booked_at}-${index}`;
  const toggle = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });

  const header = (
    <Flex
      alignItems={{ default: 'alignItemsCenter' }}
      spaceItems={{ default: 'spaceItemsMd' }}
      className="pf-v6-u-mb-md"
    >
      <FlexItem>
        <MonthPicker month={month} onChange={onMonthChange} />
      </FlexItem>
      <FlexItem>
        <RefreshButton loading={loading} onRefresh={() => void refetch()} />
      </FlexItem>
      <FlexItem>
        <FetchedAt fetchedAt={fetchedAt} />
      </FlexItem>
    </Flex>
  );

  if (data === null) {
    return (
      <>
        {header}
        {error !== null && !loading ? (
          <ReadErrorState
            title="Impossible de charger le journal Mollie"
            message={error}
            onRetry={() => void refetch()}
          />
        ) : (
          <LiveReadSpinner what="journal Mollie" />
        )}
      </>
    );
  }

  const { totals, check } = data;
  const filters = (
    <>
      <ToolbarItem>
        <StatusMultiSelect
          options={typeOptions}
          selected={types}
          onChange={setTypes}
          ariaLabel="Filtrer par type de mouvement"
          label="Type de mouvement"
        />
      </ToolbarItem>
      <ToolbarItem>
        <StatusMultiSelect
          options={purposeOptions}
          selected={purposes}
          onChange={setPurposes}
          ariaLabel="Filtrer par objet"
          label="Objet"
        />
      </ToolbarItem>
      <ToolbarItem>
        <ChoiceFilter
          ariaLabel="Filtrer par versement"
          allLabel="Tous les versements"
          value={settlement}
          options={settlementOptions}
          onChange={setSettlement}
        />
      </ToolbarItem>
      <ToolbarItem alignSelf="center">
        <Switch
          id="journal-flagged-only"
          label="Signalées seulement"
          isChecked={flaggedOnly}
          onChange={(_e, checked) => onFlaggedOnlyChange(checked)}
        />
      </ToolbarItem>
    </>
  );

  return (
    <>
      {header}
      {error !== null && <StaleDataAlert message={error} />}
      <ReconciliationBanners
        warnings={data.warnings}
        context={{ period: data.period, check }}
        onRetry={() => void refetch()}
      />
      <DescriptionList
        isCompact
        isHorizontal
        columnModifier={{ default: '1Col', md: '2Col', xl: '3Col' }}
        className="pf-v6-u-mb-md"
      >
        <DescriptionListGroup>
          <DescriptionListTerm>Mouvements du mois</DescriptionListTerm>
          <DescriptionListDescription>{totals.count}</DescriptionListDescription>
        </DescriptionListGroup>
        <DescriptionListGroup>
          <DescriptionListTerm>Brut · Frais · Net du mois</DescriptionListTerm>
          <DescriptionListDescription>
            <Money value={totals.gross} /> · <Money value={totals.fees} /> ·{' '}
            <strong>
              <Money value={totals.net} />
            </strong>
          </DescriptionListDescription>
        </DescriptionListGroup>
        <DescriptionListGroup>
          <DescriptionListTerm>Contrôle avec le rapport Mollie</DescriptionListTerm>
          <DescriptionListDescription>
            {check === null ? '—' : <CheckLabel ok={check.ok} difference={check.difference} />}
          </DescriptionListDescription>
        </DescriptionListGroup>
      </DescriptionList>
      {rows.length === 0 ? (
        <EmptyListState title="Aucun mouvement ce mois-ci" />
      ) : (
        <>
          <ListToolbar
            list={list}
            placeholder="Rechercher par client, facture, description, identifiant Mollie, domaine ou versement..."
            filters={filters}
            actions={
              <CsvButton
                label="Exporter le mois (CSV)"
                filename={reconciliationCsvFilename.journal(month)}
                build={() => buildJournalCsv(rows)}
              />
            }
          />
          {list.filtered.length === 0 ? (
            <NoMatchState onClear={clearFilters} />
          ) : (
            <>
              <Table aria-label="Journal du solde Mollie" variant="compact" isExpandable>
                <Thead>
                  <Tr>
                    <Th screenReaderText="Détails" />
                    <Th>Date</Th>
                    <Th>Type</Th>
                    <Th>Objet</Th>
                    <Th>Client</Th>
                    <Th>Référence</Th>
                    <Th className={AMOUNT_CELL}>Brut</Th>
                    <Th className={AMOUNT_CELL}>Frais</Th>
                    <Th className={AMOUNT_CELL}>Net</Th>
                    <Th>Versement</Th>
                    <Th>Signalements</Th>
                  </Tr>
                </Thead>
                {list.paginated.map((t, rowIndex) => {
                  const key = rowKey(t, rowIndex);
                  const isExpanded = expanded.has(key);
                  return (
                    <Tbody key={key} isExpanded={isExpanded}>
                      <Tr isContentExpanded={isExpanded}>
                        <Td expand={{ rowIndex, isExpanded, onToggle: () => toggle(key), expandId: 'journal-row' }} />
                        <Td dataLabel="Date" modifier="nowrap">
                          {formatBareDateNumeric(t.booking_date)}
                        </Td>
                        <Td dataLabel="Type">{labelOf(TRANSACTION_TYPE_LABELS, t.type)}</Td>
                        <Td dataLabel="Objet">{t.purpose === null ? '—' : labelOf(PURPOSE_LABELS, t.purpose)}</Td>
                        <Td dataLabel="Client">{t.client_name ?? '—'}</Td>
                        <Td dataLabel="Référence" modifier="breakWord">
                          {referenceText(t)}
                        </Td>
                        <Td dataLabel="Brut" className={AMOUNT_CELL}>
                          <Money value={t.gross} />
                        </Td>
                        <Td dataLabel="Frais" className={AMOUNT_CELL}>
                          <Money value={t.fees} />
                        </Td>
                        <Td dataLabel="Net" className={AMOUNT_CELL}>
                          <Money value={t.net} />
                        </Td>
                        <Td dataLabel="Versement">
                          <SettlementRef
                            id={t.settlement_id}
                            reference={t.settlement_reference}
                            inferred={t.settlement_inferred}
                            deducted={DEDUCTED_TYPES.has(t.type)}
                          />
                        </Td>
                        <Td dataLabel="Signalements">
                          <FlagLabels flags={t.flags} />
                        </Td>
                      </Tr>
                      <Tr isExpanded={isExpanded}>
                        <Td />
                        <Td colSpan={COLUMN_COUNT - 1}>
                          <ExpandableRowContent>
                            <DetailList
                              items={[
                                { term: 'Description du paiement', value: t.payment_description },
                                {
                                  term: 'Moyen de paiement',
                                  value:
                                    t.payment_method === null ? null : labelOf(PAYMENT_METHOD_LABELS, t.payment_method),
                                },
                                {
                                  term: 'Payé le',
                                  value: t.payment_paid_at === null ? null : formatInstantBrussels(t.payment_paid_at),
                                },
                                { term: 'Comptabilisé le', value: formatInstantBrussels(t.booked_at) },
                                { term: 'Créé chez Mollie le', value: formatInstantBrussels(t.created_at) },
                                {
                                  term: 'Versé le',
                                  value:
                                    t.settlement_settled_at === null
                                      ? null
                                      : formatInstantBrussels(t.settlement_settled_at),
                                },
                                { term: 'Paiement Mollie', value: t.payment_id },
                                {
                                  term: 'Remboursement',
                                  value:
                                    t.refund_id === null
                                      ? null
                                      : [t.refund_id, t.refund_description].filter(Boolean).join(' — '),
                                },
                                {
                                  term: 'Rétrofacturation',
                                  value:
                                    t.chargeback_id === null
                                      ? null
                                      : [t.chargeback_id, t.chargeback_description].filter(Boolean).join(' — '),
                                },
                                {
                                  term: 'Facture Mollie',
                                  value: t.mollie_invoice_reference ?? t.mollie_invoice_id,
                                },
                                ...attributionItems(t),
                                { term: 'Mouvement Mollie', value: t.id },
                              ]}
                            />
                          </ExpandableRowContent>
                        </Td>
                      </Tr>
                    </Tbody>
                  );
                })}
              </Table>
              <ListPagination list={list} variant="bottom" />
            </>
          )}
        </>
      )}
    </>
  );
};

export default MollieJournalTab;
